import { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import type { DateRangeKey, CustomDateRange } from '@/hooks/useSemData';

interface DateRangeSelectorProps {
  value: DateRangeKey;
  onChange: (range: DateRangeKey) => void;
  customRange?: CustomDateRange;
  onCustomRangeChange?: (range: CustomDateRange) => void;
}

const PRESETS: { key: Exclude<DateRangeKey, 'custom'>; label: string }[] = [
  { key: '7d', label: '7 days' },
  { key: '30d', label: '30 days' },
  { key: '60d', label: '60 days' },
  { key: '90d', label: '90 days' },
];

function toDateStr(d: Date): string {
  return d.toISOString().split('T')[0];
}

function parseDate(s: string): Date {
  return new Date(s + 'T00:00:00');
}

function formatShort(s: string): string {
  const d = parseDate(s);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatLabel(rangeKey: DateRangeKey, custom?: CustomDateRange): string {
  if (rangeKey === 'custom' && custom) {
    return `${formatShort(custom.start)} – ${formatShort(custom.end)}`;
  }
  const preset = PRESETS.find(p => p.key === rangeKey);
  return preset?.label || '30 days';
}

// ============================================
// Mini Calendar Grid
// ============================================

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfWeek(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

interface CalendarMonthProps {
  year: number;
  month: number;
  selStart: string | null;
  selEnd: string | null;
  hoverDate: string | null;
  onDateClick: (date: string) => void;
  onDateHover: (date: string | null) => void;
  maxDate: string;
  minDate: string;
}

function CalendarMonth({ year, month, selStart, selEnd, hoverDate, onDateClick, onDateHover, maxDate, minDate }: CalendarMonthProps) {
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfWeek(year, month);
  const monthName = new Date(year, month, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  // Determine effective end for highlight range
  const effectiveEnd = selEnd || hoverDate;

  return (
    <div className="select-none">
      <div className="text-center text-xs font-medium text-[var(--ed-t2)] mb-2">{monthName}</div>
      <div className="grid grid-cols-7 gap-0">
        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
          <div key={d} className="text-center text-[10px] text-[var(--ed-t4)] font-medium py-1">{d}</div>
        ))}
        {cells.map((day, i) => {
          if (day === null) return <div key={`e${i}`} />;
          const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const disabled = dateStr > maxDate || dateStr < minDate;

          const isStart = dateStr === selStart;
          const isEnd = dateStr === (selEnd || null);
          const inRange = selStart && effectiveEnd && dateStr >= selStart && dateStr <= effectiveEnd;
          const isToday = dateStr === toDateStr(new Date());

          return (
            <button
              key={dateStr}
              disabled={disabled}
              onClick={() => onDateClick(dateStr)}
              onMouseEnter={() => onDateHover(dateStr)}
              onMouseLeave={() => onDateHover(null)}
              className={`
                relative h-7 w-full text-[11px] tabular-nums rounded transition-colors
                ${disabled ? 'text-[var(--ed-t4)]/30 cursor-not-allowed' : 'cursor-pointer hover:bg-[var(--ed-bg-3)]'}
                ${isStart || isEnd ? 'bg-[var(--ed-t1)] text-[var(--ed-bg-0)] font-semibold hover:bg-[var(--ed-t1)]' : ''}
                ${inRange && !isStart && !isEnd ? 'bg-white/[0.06] text-[var(--ed-t1)]' : ''}
                ${!inRange && !isStart && !isEnd && !disabled ? 'text-[var(--ed-t2)]' : ''}
                ${isToday && !isStart && !isEnd ? 'ring-1 ring-inset ring-[var(--ed-t4)]/30' : ''}
              `}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ============================================
// Main DateRangeSelector
// ============================================

export function DateRangeSelector({ value, onChange, customRange, onCustomRangeChange }: DateRangeSelectorProps) {
  const [open, setOpen] = useState(false);
  const [pickStart, setPickStart] = useState<string | null>(null);
  const [pickEnd, setPickEnd] = useState<string | null>(null);
  const [hoverDate, setHoverDate] = useState<string | null>(null);
  const [viewYear, setViewYear] = useState(() => new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(() => new Date().getMonth() - 1); // show prev + current
  const dropdownRef = useRef<HTMLDivElement>(null);

  const maxDate = toDateStr(new Date(Date.now() - 86400000)); // yesterday
  const minDate = '2025-12-19'; // earliest data

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Initialize picker from current selection
  useEffect(() => {
    if (open && value === 'custom' && customRange) {
      setPickStart(customRange.start);
      setPickEnd(customRange.end);
    } else if (open) {
      setPickStart(null);
      setPickEnd(null);
    }
  }, [open]);

  function handleDateClick(date: string) {
    if (!pickStart || (pickStart && pickEnd)) {
      // Start new selection
      setPickStart(date);
      setPickEnd(null);
    } else {
      // Complete selection
      if (date < pickStart) {
        setPickEnd(pickStart);
        setPickStart(date);
        applyCustomRange(date, pickStart);
      } else {
        setPickEnd(date);
        applyCustomRange(pickStart, date);
      }
    }
  }

  function applyCustomRange(start: string, end: string) {
    onCustomRangeChange?.({ start, end });
    onChange('custom');
    setTimeout(() => setOpen(false), 200);
  }

  function handlePreset(key: Exclude<DateRangeKey, 'custom'>) {
    onChange(key);
    setOpen(false);
  }

  function prevMonth() {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); }
    else setViewMonth(viewMonth - 1);
  }

  function nextMonth() {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); }
    else setViewMonth(viewMonth + 1);
  }

  const nextMonthIdx = viewMonth === 11 ? 0 : viewMonth + 1;
  const nextMonthYear = viewMonth === 11 ? viewYear + 1 : viewYear;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger */}
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-colors text-[0.75rem] ${
          open
            ? 'bg-[var(--ed-bg-3)] border-[var(--ed-rule-heavy)] text-[var(--ed-t1)]'
            : 'bg-[var(--ed-bg-2)] border-[var(--ed-rule)] text-[var(--ed-t2)] hover:text-[var(--ed-t1)] hover:border-[var(--ed-rule-heavy)]'
        }`}
      >
        <Calendar className="h-3.5 w-3.5" strokeWidth={1.5} />
        <span className="font-medium">{formatLabel(value, customRange)}</span>
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute right-0 top-full mt-2 z-50 bg-[var(--ed-bg-1)] border border-[var(--ed-rule-heavy)] rounded-xl shadow-2xl shadow-black/40 p-4 w-[520px]">
          {/* Presets row */}
          <div className="flex items-center gap-1 mb-4">
            {PRESETS.map((p) => (
              <button
                key={p.key}
                onClick={() => handlePreset(p.key)}
                className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${
                  value === p.key
                    ? 'bg-[var(--ed-bg-3)] text-[var(--ed-t1)] border border-[var(--ed-rule-heavy)]'
                    : 'text-[var(--ed-t3)] hover:text-[var(--ed-t1)] hover:bg-[var(--ed-bg-2)]'
                }`}
              >
                {p.label}
              </button>
            ))}
            <div className="flex-1" />
            {value === 'custom' && customRange && (
              <span className="text-[10px] text-[var(--ed-t3)] tabular-nums">
                {formatShort(customRange.start)} – {formatShort(customRange.end)}
              </span>
            )}
          </div>

          {/* Month navigation */}
          <div className="flex items-center justify-between mb-3">
            <button onClick={prevMonth} className="p-1.5 rounded-lg hover:bg-[var(--ed-bg-3)] text-[var(--ed-t3)] hover:text-[var(--ed-t1)] transition-colors">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="flex-1" />
            <button onClick={nextMonth} className="p-1.5 rounded-lg hover:bg-[var(--ed-bg-3)] text-[var(--ed-t3)] hover:text-[var(--ed-t1)] transition-colors">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          {/* Dual calendar */}
          <div className="grid grid-cols-2 gap-6">
            <CalendarMonth
              year={viewYear}
              month={viewMonth}
              selStart={pickStart}
              selEnd={pickEnd}
              hoverDate={pickStart && !pickEnd ? hoverDate : null}
              onDateClick={handleDateClick}
              onDateHover={setHoverDate}
              maxDate={maxDate}
              minDate={minDate}
            />
            <CalendarMonth
              year={nextMonthYear}
              month={nextMonthIdx}
              selStart={pickStart}
              selEnd={pickEnd}
              hoverDate={pickStart && !pickEnd ? hoverDate : null}
              onDateClick={handleDateClick}
              onDateHover={setHoverDate}
              maxDate={maxDate}
              minDate={minDate}
            />
          </div>

          {/* Selection hint */}
          <div className="mt-3 pt-3 border-t border-[var(--ed-rule)] text-center">
            <span className="text-[10px] text-[var(--ed-t4)]">
              {!pickStart ? 'Click a start date' : !pickEnd ? 'Click an end date' : 'Range selected'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
